/**
 * Developer Metrics constants and definitions
 * Used across the Developer Metrics section of Industry Metrics
 */

export const DEVELOPER_METRICS = {
  // Downloads
  npmDownloads: { label: 'npm Downloads', group: 'Downloads', format: 'number' },
  pypiDownloads: { label: 'PyPI Downloads', group: 'Downloads', format: 'number' },
  nugetDownloads: { label: 'NuGet Downloads', group: 'Downloads', format: 'number' },
  dockerPulls: { label: 'Docker Pulls', group: 'Downloads', format: 'number' },
  terraformDownloads: { label: 'Terraform Downloads', group: 'Downloads', format: 'number' },
};

export const METRIC_GROUPS = ['Downloads'];

// Metrics whose exported weekly value is already a rolling/indexed point-in-time
// measure. These aggregate across packages by summing values for the same week,
// then use the latest week for display and delta comparisons.
export const POINT_IN_TIME_METRICS = new Set([
]);

export const isPointInTimeMetric = (metricKey) => POINT_IN_TIME_METRICS.has(metricKey);

/**
 * Get metrics grouped by their category
 */
export const getMetricsByGroup = () => {
  const grouped = {};
  METRIC_GROUPS.forEach(group => {
    grouped[group] = Object.entries(DEVELOPER_METRICS)
      .filter(([_, config]) => config.group === group)
      .map(([key, config]) => ({ key, ...config }));
  });
  return grouped;
};

/**
 * Get all metric keys as an array
 */
export const getMetricKeys = () => Object.keys(DEVELOPER_METRICS);

/**
 * Get metric label by key
 */
export const getMetricLabel = (key) => DEVELOPER_METRICS[key]?.label || key;

/**
 * Get metric format by key
 */
export const getMetricFormat = (key) => DEVELOPER_METRICS[key]?.format || 'number';

/**
 * Developer Metrics View Types
 */
export const DEV_VIEWS = {
  OVERVIEW: 'overview',
  DETAIL: 'detail',
  LEADERBOARD: 'leaderboard',
  DAILY: 'daily',
};

/**
 * Display categories (nested level only - not App/Infra parent level)
 */
export const DISPLAY_CATEGORIES = [
  'AI Tools, IaaS, APIs & Low Code',
  'Commerce & POS',
  'CRM, Marketing & Support',
  'Database & Data Platforms',
  'DevTools & IT Ops',
  'Digital Payment Platforms',
  'Engineering & Industrials',
  'HR, Finance, Legal & SCM',
  'Networking & CDN',
  'Other Vertical',
  'Productivity & Content Creation',
  'Security, Compliance & UEM',
  'Specialty Data & Intelligence',
  'Other',
];

/**
 * Leaderboard-specific metrics for ranking
 */
export const LEADERBOARD_METRICS = [
  { key: 'npmDownloads', label: 'npm Downloads' },
  { key: 'pypiDownloads', label: 'PyPI Downloads' },
  { key: 'nugetDownloads', label: 'NuGet Downloads' },
  { key: 'dockerPulls', label: 'Docker Pulls' },
  { key: 'terraformDownloads', label: 'Terraform Downloads' },
];

/**
 * Project categories for filtering
 */
export const PROJECT_CATEGORIES = [
  'streaming',
  'database',
  'observability',
  'security',
  'ci-cd',
  'search',
  'identity',
  'collaboration',
];

/**
 * Default metrics to show in Overview table
 */
export const OVERVIEW_DEFAULT_METRICS = [
  'npmDownloads',
  'pypiDownloads',
  'nugetDownloads',
  'dockerPulls',
  'terraformDownloads',
];

/**
 * Format large numbers for display
 */
const DEVELOPER_METRIC_COMPACT_UNITS = [
  { divisor: 1e9, suffix: 'B' },
  { divisor: 1e6, suffix: 'M' },
  { divisor: 1e3, suffix: 'k' },
  { divisor: 1, suffix: '' },
];

const formatDeveloperMetricNumber = (numericValue) => {
  const absoluteValue = Math.abs(numericValue);
  let unitIndex = DEVELOPER_METRIC_COMPACT_UNITS.findIndex(({ divisor }) => absoluteValue >= divisor);
  if (unitIndex < 0) unitIndex = DEVELOPER_METRIC_COMPACT_UNITS.length - 1;

  let unit = DEVELOPER_METRIC_COMPACT_UNITS[unitIndex];
  let scaledValue = numericValue / unit.divisor;
  let maximumFractionDigits = Math.abs(scaledValue) >= 100 ? 0 : 1;
  let roundedValue = Number(scaledValue.toFixed(maximumFractionDigits));

  if (Math.abs(roundedValue) >= 1000 && unitIndex > 0) {
    unit = DEVELOPER_METRIC_COMPACT_UNITS[unitIndex - 1];
    scaledValue = numericValue / unit.divisor;
    maximumFractionDigits = Math.abs(scaledValue) >= 100 ? 0 : 1;
    roundedValue = Number(scaledValue.toFixed(maximumFractionDigits));
  }

  const normalizedValue = Object.is(roundedValue, -0) ? 0 : roundedValue;
  return `${normalizedValue.toLocaleString('en-US', { maximumFractionDigits })}${unit.suffix}`;
};

export const formatMetricValue = (value, format = 'number') => {
  const numericValue = Number(value);
  if (value == null || !Number.isFinite(numericValue)) return '–';

  if (format === 'number') {
    return formatDeveloperMetricNumber(numericValue);
  }

  return numericValue.toLocaleString('en-US');
};

/**
 * Calculate delta percentage between two values
 */
export const calculateDelta = (current, previous) => {
  if (!previous || previous === 0 || current == null) return null;
  return (current - previous) / previous;
};

/**
 * Get the ecosystem color for charts
 */
export const ECOSYSTEM_COLORS = {
  npm: '#CB3837',
  pypi: '#3775A9',
  nuget: '#004880',
  docker: '#2496ED',
  terraform: '#7B42BC',
};

/**
 * Developer-specific subcategories for enhanced filtering hierarchy
 * Provides 3rd-level granularity within SoftwareIQ categories when on Developer tab.
 * Keyed by leaf category label strings (matching filterCategories.js).
 */
export const DEV_SUBCATEGORIES = {
  'Database & Data Platforms': [
    { id: 'database', label: 'Core Database' },
    { id: 'search', label: 'Search & Analytics' },
    { id: 'streaming', label: 'Streaming & Events' },
    { id: 'ingestion', label: 'Data Ingestion' },
  ],
  'DevTools & IT Ops': [
    { id: 'observability', label: 'Observability' },
    { id: 'devops', label: 'DevOps & GitOps' },
    { id: 'ci-cd', label: 'CI/CD' },
  ],
  'AI Tools, IaaS, APIs & Low Code': [
    { id: 'cloud', label: 'Cloud & IaaS' },
    { id: 'serverless', label: 'Serverless' },
  ],
  'Security, Compliance & UEM': [
    { id: 'security', label: 'Security' },
    { id: 'identity', label: 'Identity & Access' },
  ],
  'Networking & CDN': [
    { id: 'networking', label: 'Networking' },
  ],
};
